import {resolveCloneThreadLabel, resolveClonedDefaultUsers, resolveCloneItemMeta} from '../clone-display';
import {
  unpackAclMap,
  unpackBindsMap,
  setByPath,
  getByPath,
  BIND_EXECUTOR,
  BIND_CREATED_AT,
  BIND_CLONE_VALUE,
  BIND_OPINION,
  BIND_AGREE,
} from '../flow-acl';

/**
 * 流程本地模拟运行时（用于流程设计器「测试」）
 *
 * 能力：
 * - init：创建本地实例、解析图、提取参与人
 * - patch：身份鉴权 + action/data + 本地状态推进（含 weak 跨过）
 * - cancel：撤回最近一次尚未被后续操作覆盖的提交
 */

function uid(prefix = 'h') {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function toPlain(value) {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    if (Array.isArray(value)) {
      return value.map((v) => toPlain(v));
    }
    const out = {};
    Object.keys(value || {}).forEach((k) => {
      if (k.startsWith('__') || k.startsWith('$')) return;
      const v = value[k];
      if (typeof v === 'function' || typeof v === 'undefined') return;
      if (v && typeof v === 'object' && (v._isVue || v.$el)) return;
      out[k] = toPlain(v);
    });
    return out;
  }
}

function asIdList(v) {
  if (v == null || v === '') return [];
  if (Array.isArray(v)) {
    return v.map((x) => {
      if (x == null) return null;
      if (typeof x === 'object') return x.id || x.user_id || x.uid || null;
      return x;
    }).filter(Boolean).map(String);
  }
  if (typeof v === 'object') {
    const id = v.id || v.user_id || v.uid;
    return id != null ? [String(id)] : [];
  }
  return [String(v)];
}

function nodeType(n) {
  return n?.type || n?.graph?.type || '';
}

function nodeOption(n) {
  return n?.properties || n?.option || {};
}

function nodeLabel(n) {
  const opt = nodeOption(n);
  return opt.name || n?.text?.value || n?.graph?.text?.value || n?.id || '节点';
}

/** 审批节点时效时长（毫秒）；properties.timeout 统一为秒 */
function resolveTimeoutMs(opt = {}) {
  const sec = Number(opt.timeout);
  if (!sec || sec <= 0 || Number.isNaN(sec)) return null;
  return sec * 1000;
}

function resolveTimeoutType(opt = {}) {
  return opt.timeoutType === '强制关闭' ? '强制关闭' : '仅预警';
}

export default class FlowLocalRuntime {
  /**
   * @param {object} opts
   * @param {object} opts.flowDef 流程定义（名称、图标等）
   * @param {object} opts.graph LogicFlow 图 { nodes, edges } 或版本 { nodes, actions }
   * @param {string|number} [opts.actorId] 当前操作人
   * @param {Array} [opts.userPool] 可选用户池（用于解析姓名）
   */
  constructor(opts = {}) {
    this.flowDef = toPlain(opts.flowDef || {});
    this.userPool = opts.userPool || [];
    this.deps = opts.deps || [];
    this.actorId = opts.actorId != null ? String(opts.actorId) : null;

    const normalized = FlowLocalRuntime.normalizeGraph(opts.graph || {});
    this.nodes = normalized.nodes;
    this.edges = normalized.edges;

    this.instance = null;
    this._cancelStack = [];
  }

  static normalizeGraph(graph = {}) {
    let nodes = [];
    let edges = [];

    if (Array.isArray(graph.nodes) && graph.nodes.length && graph.nodes[0].graph) {
      // API version: nodes[].graph / option, actions[]
      nodes = graph.nodes.map((n) => ({
        id: n.id,
        type: n.graph?.type || n.type,
        x: n.graph?.x,
        y: n.graph?.y,
        text: n.graph?.text,
        properties: toPlain({...(n.option || {}), users: n.users}),
      }));
      const actions = graph.actions || graph.edges || [];
      edges = actions.map((e) => ({
        id: e.id,
        sourceNodeId: e.from || e.sourceNodeId,
        targetNodeId: e.to || e.targetNodeId,
        type: e.graph?.type || e.type || 'submit',
        properties: toPlain(e.option || e.properties || {}),
        text: e.graph?.text || e.text,
      }));
    } else {
      nodes = (graph.nodes || []).map((n) => ({
        ...n,
        properties: toPlain(n.properties || {}),
      }));
      edges = (graph.edges || []).map((e) => ({
        ...e,
        sourceNodeId: e.sourceNodeId || e.from,
        targetNodeId: e.targetNodeId || e.to,
        properties: toPlain(e.properties || {}),
      }));
    }

    return {nodes, edges};
  }

  /**
   * 节点执行态：done / rejected / pending / idle
   * 供执行页示意图着色
   */
  getNodeStatusMap() {
    if (!this.instance) return {};
    const map = {};
    const tasks = this.instance.tasks || [];
    const completed = new Set(this.instance.completedNodeIds || []);
    (this.nodes || []).forEach((n) => {
      const nodeTasks = tasks.filter((t) => t.nodeId === n.id);
      if (nodeTasks.some((t) => t.status === 'rejected')) {
        map[n.id] = 'rejected';
      } else if (nodeTasks.some((t) => t.status === 'pending')) {
        map[n.id] = 'pending';
      } else if (
        completed.has(n.id) ||
        (nodeTasks.length > 0 &&
          nodeTasks.every((t) =>
            t.status === 'done' || t.status === 'skipped' || t.status === 'cancelled'))
      ) {
        map[n.id] = 'done';
      } else {
        map[n.id] = 'idle';
      }
    });
    return map;
  }

  /** 克隆节点：是否已有部分线程完成（仍有 pending） */
  getClonePartialDoneMap() {
    if (!this.instance) return {};
    const map = {};
    const tasks = this.instance.tasks || [];
    (this.nodes || []).forEach((n) => {
      const nodeTasks = tasks.filter((t) => t.nodeId === n.id);
      if (nodeTasks.length < 2) return;
      const hasDone = nodeTasks.some((t) =>
        t.status === 'done' || t.status === 'skipped');
      const hasPending = nodeTasks.some((t) => t.status === 'pending');
      if (hasDone && hasPending) {
        map[n.id] = true;
      }
    });
    return map;
  }

  /** 流程图 LogicFlow 数据（运行态注入 _execStatus / _clonePartialDone） */
  getGraphData() {
    const statusMap = this.getNodeStatusMap();
    const partialMap = this.getClonePartialDoneMap();
    const nodes = (this.nodes || []).map((n) => {
      const props = {...(n.properties || {})};
      // 清掉旧态，避免配置态残留
      delete props._execStatus;
      delete props._clonePartialDone;
      if (statusMap[n.id]) {
        props._execStatus = statusMap[n.id];
      }
      if (partialMap[n.id]) {
        props._clonePartialDone = true;
      }
      return {...n, properties: props};
    });
    const edges = (this.edges || []).map((e) => {
      const props = {...(e.properties || {})};
      const target = nodes.find((n) => n.id === e.targetNodeId);
      if (target && (target.type === 'carbon_node' ||
        (target.graph && target.graph.type === 'carbon_node'))) {
        props._toCarbon = true;
      }
      return {...e, properties: props};
    });
    return {nodes, edges};
  }

  getNode(id) {
    return this.nodes.find((n) => n.id === id) || null;
  }

  getStartNode() {
    return this.nodes.find((n) => nodeType(n) === 'start_node') || null;
  }

  getOutgoing(nodeId, actionType) {
    return this.edges.filter((e) => {
      if (e.sourceNodeId !== nodeId) return false;
      if (!actionType) return true;
      const t = e.properties?.type || e.type || 'submit';
      // submit / approve 视为前进；reject 为退回
      if (actionType === 'submit' || actionType === 'approve') {
        return t === 'submit' || t === 'approve' || !e.properties?.type;
      }
      if (actionType === 'reject') {
        return t === 'reject';
      }
      return t === actionType;
    });
  }

  getIncoming(nodeId) {
    return this.edges.filter((e) => e.targetNodeId === nodeId);
  }

  /**
   * 提取流程中配置过的人员（默认审批人/抄送人等）
   */
  collectActors() {
    const map = {};
    const add = (id, meta = {}) => {
      if (id == null || id === '') return;
      const sid = String(id);
      if (!map[sid]) {
        const u = this.userPool.find((x) => String(x.id) === sid);
        map[sid] = {
          id: sid,
          name: u?.name || meta.name || sid,
          avatar: u?.avatar,
          roles: new Set(),
          nodeIds: new Set(),
        };
      }
      if (meta.role) map[sid].roles.add(meta.role);
      if (meta.nodeId) map[sid].nodeIds.add(meta.nodeId);
    };

    if (this.actorId) {
      add(this.actorId, {role: '当前用户'});
    }

    this.nodes.forEach((n) => {
      const t = nodeType(n);
      const opt = nodeOption(n);
      const label = nodeLabel(n);
      const role =
        t === 'start_node' ? '发起' :
        t === 'approval_node' ? '审批' :
        t === 'carbon_node' ? '抄送' : t;

      asIdList(opt['default-users']).forEach((id) => {
        add(id, {role, nodeId: n.id, name: label});
      });
      asIdList(opt.users).forEach((id) => {
        add(id, {role, nodeId: n.id});
      });
    });

    // 若几乎没配人，从用户池补几个便于测试切换
    const list = Object.values(map).map((a) => ({
      ...a,
      roles: [...a.roles],
      nodeIds: [...a.nodeIds],
    }));
    if (list.length < 2 && this.userPool.length) {
      this.userPool.slice(0, 5).forEach((u) => {
        if (!map[String(u.id)]) {
          list.push({
            id: String(u.id),
            name: u.name,
            avatar: u.avatar,
            roles: ['测试用户'],
            nodeIds: [],
          });
        }
      });
    }
    return list;
  }

  /**
   * 从初始化后的 nodeData 提取可切换身份（含 clone 分线程）
   * 不补充用户池/默认人，只反映配置阶段选定的人员 + 发起人
   */
  collectActorsFromNodeData(nodeData = {}) {
    const map = {};
    const add = (id, meta = {}) => {
      if (id == null || id === '') return;
      const sid = String(id);
      if (!map[sid]) {
        const u = this.userPool.find((x) => String(x.id) === sid);
        map[sid] = {
          id: sid,
          name: u?.name || meta.name || sid,
          avatar: u?.avatar,
          roles: new Set(),
          nodeIds: new Set(),
        };
      }
      if (meta.role) map[sid].roles.add(meta.role);
      if (meta.nodeId) map[sid].nodeIds.add(meta.nodeId);
    };

    const creatorId = this.instance?.created_by || this.actorId;
    if (creatorId) add(creatorId, {role: '发起人'});

    Object.keys(nodeData || {}).forEach((nodeId) => {
      const nd = nodeData[nodeId] || {};
      const node = this.getNode(nodeId);
      const t = nodeType(node);
      const role =
        t === 'start_node' ? '发起' :
        t === 'carbon_node' ? '抄送' :
        t === 'approval_node' ? '审批' : (t || '节点');
      asIdList(nd.executors).forEach((id) => add(id, {role, nodeId}));
      const cloned = nd.cloned || {};
      Object.keys(cloned).forEach((threadId) => {
        const conf = cloned[threadId] || {};
        const label = threadId.startsWith(`${nodeId}_`) ?
          threadId.slice(nodeId.length + 1) :
          threadId;
        asIdList(conf.executors).forEach((id) => {
          add(id, {role: `${role}·${label}`, nodeId: threadId});
        });
      });
    });

    return Object.values(map).map((a) => ({
      ...a,
      roles: [...a.roles],
      nodeIds: [...a.nodeIds],
    }));
  }

  /** 当前身份可见的 pending 任务（仅自己是执行人的；抄送通知不进待办） */
  getPendingTasksForActor(actorId) {
    if (!this.instance) return [];
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!aid) return [];
    return (this.instance.tasks || []).filter((t) => {
      if (t.status !== 'pending') return false;
      if (t.type === 'carbon_node') return false;
      const node = this.getNode(t.nodeId);
      if (!node) return false;
      if (nodeType(node) === 'carbon_node') return false;
      return this._taskAssignees(t, node).includes(aid);
    });
  }

  /** 克隆节点是否开启「平行可见」（默认可视为 false） */
  _isParallelVisible(node) {
    return !!(node && nodeOption(node).parallelVisible);
  }

  /** 当前身份是否为该任务执行人 */
  _isActorAssignee(actorId, task, node) {
    if (!actorId || !task || !node) return false;
    return this._taskAssignees(task, node).includes(String(actorId));
  }

  /**
   * 平行线程可见性：
   * - 非平行任务：可见
   * - 本人执行人：可见
   * - 发起人：可见（非执行人时只读）
   * - 节点开启 parallelVisible：可见平行兄弟任务
   * - 否则不可见
   */
  _canSeeParallelTask(actorId, task, node) {
    if (!task || !node) return false;
    if (!task.parallel) return true;
    if (this._isActorAssignee(actorId, task, node)) return true;
    if (this.instance && String(this.instance.created_by) === String(actorId)) return true;
    return this._isParallelVisible(node);
  }

  /**
   * 展示用待办：本人可处理 +（平行可见时）同节点平行兄弟 pending
   * 兄弟任务带 peerView=true，仅可查看不可填写
   */
  getVisibleTasksForActor(actorId) {
    if (!this.instance) return [];
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!aid) return [];
    return (this.instance.tasks || []).filter((t) => {
      if (t.status !== 'pending') return false;
      if (t.type === 'carbon_node') return false;
      const node = this.getNode(t.nodeId);
      if (!node) return false;
      if (nodeType(node) === 'carbon_node') return false;
      return this._canSeeParallelTask(aid, t, node);
    }).map((t) => {
      const node = this.getNode(t.nodeId);
      const mine = this._isActorAssignee(aid, t, node);
      return {
        ...t,
        peerView: !!(t.parallel && !mine),
      };
    });
  }

  /** 当前身份在某克隆节点上所属的 threadId 集合 */
  _actorThreadIdsOnNode(actorId, nodeId) {
    const aid = String(actorId || '');
    const node = this.getNode(nodeId);
    if (!aid || !node) return new Set();
    const ids = new Set();
    (this.instance?.tasks || []).forEach((t) => {
      if (t.nodeId !== nodeId || !t.threadId) return;
      if (this._isActorAssignee(aid, t, node)) ids.add(t.threadId);
    });
    const nd = (this.instance?.nodeData || {})[nodeId] || {};
    const cloned = nd.cloned || {};
    Object.keys(cloned).forEach((threadId) => {
      const list = asIdList((cloned[threadId] || {}).executors);
      if (list.includes(aid)) ids.add(threadId);
    });
    return ids;
  }

  /**
   * 历史过滤：平行不可见时，隐藏其他平行线程记录；fork 文案只保留本人线程
   */
  getVisibleHistoryForActor(actorId) {
    if (!this.instance) return [];
    const aid = actorId != null ? String(actorId) : this.actorId;
    const raw = this.instance.history || [];
    if (!aid) return toPlain(raw);

    return raw.map((h) => {
      const node = h.nodeId ? this.getNode(h.nodeId) : null;
      if (!node || !this._isCloneNode(node) || this._isParallelVisible(node)) {
        return h;
      }
      const myThreads = this._actorThreadIdsOnNode(aid, h.nodeId);
      const isCreator = this.instance && String(this.instance.created_by) === String(aid);
      if (h.action === 'fork' && myThreads.size && !isCreator) {
        const labels = [...myThreads].map((tid) => {
          const task = (this.instance.tasks || []).find((t) => t.threadId === tid);
          if (task && task.cloneKey) return task.cloneKey;
          const prefix = h.nodeId + '_';
          return tid.indexOf(prefix) === 0 ? tid.slice(prefix.length) : tid;
        });
        return {
          ...h,
          content: `启用多个子流程（${labels.join('、')}）`,
        };
      }
      return h;
    }).filter((h) => {
      const node = h.nodeId ? this.getNode(h.nodeId) : null;
      if (!node || !this._isCloneNode(node)) return true;
      if (this._isParallelVisible(node)) return true;
      // 发起人可见全部历史
      if (this.instance && String(this.instance.created_by) === String(aid)) return true;
      if (!h.threadId) {
        // fork / 无线程标记：保留（fork 文案已按本人改写）
        return true;
      }
      const myThreads = this._actorThreadIdsOnNode(aid, h.nodeId);
      // 本人从未参与该克隆节点时，仍可见全部
      if (!myThreads.size) return true;
      return myThreads.has(h.threadId);
    });
  }

  /**
   * 当前身份的查看/处理权限
   * - canView：发起人、曾参与任务、或当前待办执行人
   * - canAct：有可处理动作（同意/退回/抄送/提交，不含仅撤回）
   */
  getActorAccess(actorId) {
    if (!this.instance) {
      return {canView: true, canAct: true, canCancel: false, pendingCount: 0};
    }
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!aid) {
      return {canView: false, canAct: false, canCancel: false, pendingCount: 0};
    }

    const actions = this.getAvailableActions(aid);
    const canCancel = actions.some((a) => a.key === 'cancel');
    const canAct = actions.some((a) => a.key !== 'cancel');
    const pendingMine = this.getPendingTasksForActor(aid);
    const isCreator = String(this.instance.created_by) === aid;
    const participated = (this.instance.tasks || []).some((t) =>
      (t.assignees || []).map(String).includes(aid),
    );
    const wasCc = (this.instance.ccRecipients || []).map(String).includes(aid);
    const canView = isCreator || participated || wasCc ||
      pendingMine.length > 0 || canAct || canCancel;

    return {
      canView,
      canAct,
      canCancel,
      pendingCount: pendingMine.length,
      wasCc,
    };
  }

  /** 给 actors 列表挂上当前时刻的权限摘要 */
  _annotateActors(actors = []) {
    return (actors || []).map((a) => {
      const access = this.getActorAccess(a.id);
      return {
        ...a,
        ...access,
      };
    });
  }

  setActor(actorId) {
    this.actorId = actorId != null ? String(actorId) : null;
    if (this.instance) {
      this.instance.actorId = this.actorId;
    }
    return this.getState();
  }

  /**
   * 仅准备运行时，不创建实例（配置阶段）
   */
  prepare({actorId} = {}) {
    if (actorId != null) this.actorId = String(actorId);
    this.instance = null;
    this._cancelStack = [];
    return this.getState();
  }

  /**
   * 配置完成后启动：写入 formData/nodeData，创建实例并完成发起提交
   */
  start({formData = {}, nodeData = {}, actorId} = {}) {
    if (actorId != null) this.actorId = String(actorId);
    this.init({formData, nodeData, actorId: this.actorId});
    return this.patch({
      action: 'submit',
      data: formData,
      actorId: this.actorId,
    });
  }

  /**
   * 本地初始化（创建测试实例，停在开始节点待提交）
   * 一般由 start() 调用；也可单独 init 后再 patch
   */
    init({formData = {}, nodeData = {}, actorId} = {}) {
    if (actorId != null) this.actorId = String(actorId);
    const start = this.getStartNode();
    if (!start) {
      throw new Error('流程图缺少开始节点');
    }

    const now = new Date().toISOString();
    const plainNodeData = toPlain(nodeData) || {};
    this.instance = {
      id: uid('inst'),
      status: '待发起',
      created_at: now,
      updated_at: now,
      created_by: this.actorId,
      formData: toPlain(formData) || {},
      nodeData: plainNodeData,
      actorId: this.actorId,
      tasks: [{
        id: uid('task'),
        nodeId: start.id,
        type: 'start_node',
        name: nodeLabel(start),
        assignees: this.actorId ? [this.actorId] : [],
        status: 'pending',
        weak: false,
        parallel: false,
      }],
      completedNodeIds: [],
      ccRecipients: [],
      history: [{
        id: uid('h'),
        created_by: this.actorId || 'system',
        created_at: now,
        action: 'init',
        content: '已确认流程配置，等待发起提交',
        nodeId: start.id,
        nodeName: nodeLabel(start),
      }],
      canCancel: false,
    };
    this._cancelStack = [];
    return this.getState();
  }

  getState() {
    const nodeData = this.instance?.nodeData || {};
    // 配置阶段不展示身份条；运行阶段仅用初始化后 nodeData 提取的人员
    const rawActors = this.instance ?
      this.collectActorsFromNodeData(nodeData) :
      [];
    const actors = this.instance ? this._annotateActors(rawActors) : [];
    const access = this.instance ?
      this.getActorAccess(this.actorId) :
      {canView: true, canAct: true, canCancel: false, pendingCount: 0};
    const pendingTasks = this.instance ?
      this.getPendingTasksForActor(this.actorId) :
      [];
    const visibleTasks = this.instance ?
      this.getVisibleTasksForActor(this.actorId) :
      [];
    const visibleHistory = this.instance ?
      this.getVisibleHistoryForActor(this.actorId) :
      [];
    const instancePlain = toPlain(this.instance);
    if (instancePlain && Array.isArray(visibleHistory)) {
      instancePlain.history = visibleHistory;
    }
    return toPlain({
      flowDef: this.flowDef,
      graph: this.getGraphData(),
      actors,
      actorId: this.actorId,
      access,
      pendingTasks,
      visibleTasks,
      instance: instancePlain,
      availableActions: this.instance ? this.getAvailableActions(this.actorId) : [],
      prepared: !this.instance,
    });
  }

  /** 当前身份在本地可执行的动作 */
  getAvailableActions(actorId) {
    if (!this.instance) return [];
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!aid) return [];

    const actions = [];
    const tasks = (this.instance.tasks || []).filter((t) => t.status === 'pending');

    tasks.forEach((task) => {
      const node = this.getNode(task.nodeId);
      if (!node) return;
      if (nodeType(node) === 'carbon_node') return;
      const can = this._canActOnTask(aid, task, node);
      if (!can.ok && !can.weakSkip) return;

      const t = nodeType(node);
      if (t === 'start_node') {
        actions.push({
          key: 'submit',
          label: '提交',
          taskId: task.id,
          nodeId: task.nodeId,
          primary: true,
        });
      } else if (t === 'approval_node') {
        actions.push({
          key: 'approve',
          label: can.weakSkip ? '跳过弱审批并同意' : '同意',
          taskId: task.id,
          nodeId: task.nodeId,
          primary: true,
          weakSkip: !!can.weakSkip,
        });
        actions.push({
          key: 'reject',
          label: '退回',
          taskId: task.id,
          nodeId: task.nodeId,
          primary: false,
        });
      }
      // carbon_node：通知节点，不产生待办动作
    });

    // weak：前置 pending 为弱审批时，允许后继节点执行人直接处理后继任务（若后继尚未进 tasks，则虚拟放出）
    this._appendWeakBypassActions(aid, actions);

    const cancelable = this._findCancelEntry(aid);
    if (cancelable) {
      actions.push({
        key: 'cancel',
        label: '撤回',
        primary: false,
        cancelIndex: cancelable.index,
        nodeId: cancelable.entry.nodeId || null,
        taskId: cancelable.entry.taskId || null,
        threadId: cancelable.entry.threadId || null,
      });
    }

    return actions;
  }

  /**
   * 当前身份是否可撤回：须为全局最后一次提交（含克隆并行各线程提交），
   * 且下一节点尚未有人处理完成。
   */
  _findCancelEntry(actorId) {
    if (!this.instance || !this._cancelStack.length) return null;
    const aid = String(actorId);
    const index = this._cancelStack.length - 1;
    const entry = this._cancelStack[index];
    if (!entry || String(entry.actorId) !== aid) return null;
    if (this._hasDownstreamProgress(entry)) return null;
    return {entry, index};
  }

  /** 是否已有「下一节点」被人工处理（同节点平行线程 / 自动抄送 / 结束闭合不算阻断撤回） */
  _hasDownstreamProgress(entry) {
    if (!entry || !this.instance) return true;
    const snap = entry.snapshot;
    if (!snap) return true;
    const nodeId = entry.nodeId;
    const snapTasks = snap.tasks || [];
    const nowTasks = this.instance.tasks || [];

    for (let i = 0; i < nowTasks.length; i++) {
      const t = nowTasks[i];
      if (nodeId && t.nodeId === nodeId) continue;
      // 抄送/结束不产生人工任务；忽略遗留 carbon pending
      if (t.type === 'carbon_node' || t.type === 'finish_node') continue;
      const n = this.getNode(t.nodeId);
      if (n && (nodeType(n) === 'carbon_node' || nodeType(n) === 'finish_node')) continue;
      if (t.status !== 'done' && t.status !== 'rejected' && t.status !== 'skipped') {
        continue;
      }
      const prev = snapTasks.find((s) => s.id === t.id);
      if (!prev) return true;
      if (prev.status === 'pending' || prev.status !== t.status) return true;
    }

    const snapCompleted = new Set(snap.completedNodeIds || []);
    const nowCompleted = this.instance.completedNodeIds || [];
    for (let i = 0; i < nowCompleted.length; i++) {
      const id = nowCompleted[i];
      if (nodeId && id === nodeId) continue;
      if (snapCompleted.has(id)) continue;
      // 自动闭合的结束节点、自动抄送节点不阻断上一节点撤回
      const n = this.getNode(id);
      if (n && (nodeType(n) === 'finish_node' || nodeType(n) === 'carbon_node')) {
        continue;
      }
      return true;
    }
    return false;
  }

  /**
   * 撤回同节点平行线程中自己的那一份：恢复 task 为 pending，并撤销因此激活的后继
   */
  _surgicalCancelParallel(entry) {
    const taskId = entry.taskId;
    const nodeId = entry.nodeId;
    const task = (this.instance.tasks || []).find((t) => t.id === taskId);
    if (task) {
      task.status = 'pending';
      delete task.timeoutWarning;
    }

    const completed = this.instance.completedNodeIds || [];
    const cidx = completed.indexOf(nodeId);
    if (cidx >= 0) completed.splice(cidx, 1);

    const outs = this.getOutgoing(nodeId, 'submit');
    outs.forEach((edge) => {
      const nextId = edge.targetNodeId;
      this.instance.tasks = (this.instance.tasks || []).filter((t) => {
        if (t.nodeId !== nextId) return true;
        return t.status !== 'pending';
      });
      const ni = (this.instance.completedNodeIds || []).indexOf(nextId);
      if (ni >= 0) this.instance.completedNodeIds.splice(ni, 1);
    });

    // 若曾触发并行汇聚，撤销对应系统记录
    if (nodeId && this.instance.history && this.instance.history.length) {
      for (let i = this.instance.history.length - 1; i >= 0; i--) {
        const h = this.instance.history[i];
        if (h.action === 'join' && h.nodeId === nodeId) {
          this.instance.history.splice(i, 1);
          break;
        }
      }
    }

    // 回滚该次提交写入的表单绑定（用提交前快照）
    if (entry.snapshot && entry.snapshot.formData) {
      this.instance.formData = toPlain(entry.snapshot.formData) || {};
    }

    // 撤销因本节点汇聚而触发的自动抄送 / 流程闭合
    this._undoAutoFollowups(nodeId);

    this._refreshStatus();
  }

  _taskAssignees(task, node) {
    if (Array.isArray(task.assignees) && task.assignees.length) {
      return task.assignees.map(String);
    }
    const opt = nodeOption(node);
    const t = nodeType(node);
    if (t === 'start_node') {
      return this.instance?.created_by ? [String(this.instance.created_by)] : (this.actorId ? [this.actorId] : []);
    }
    // 优先用配置阶段 nodeData
    const nd = (this.instance?.nodeData || {})[node.id] || {};
    if (task.threadId && nd.cloned && nd.cloned[task.threadId]) {
      const list = asIdList(nd.cloned[task.threadId].executors);
      if (list.length) return list;
      // 回退：按克隆分类默认人
      const keyPart = String(task.threadId).indexOf(node.id + '_') === 0 ?
        String(task.threadId).slice(node.id.length + 1) :
        (task.cloneKey || '');
      const byClone = resolveClonedDefaultUsers(
        opt.clonedDefaultUsers,
        keyPart,
        opt['default-users'],
      );
      if (byClone.length) return byClone;
    }
    const fromConfig = asIdList(nd.executors);
    if (fromConfig.length) return fromConfig;
    const defaults = asIdList(opt['default-users']);
    if (defaults.length) return defaults;
    return this.actorId ? [this.actorId] : [];
  }

  _isCloneNode(node) {
    return !!(nodeOption(node).clonedBy);
  }

  /** 从 nodeData 解析 clone 并行线程 */
  _getCloneThreads(node) {
    const nodeId = node.id;
    const nd = (this.instance?.nodeData || {})[nodeId] || {};
    const cloned = nd.cloned || {};
    const opt = nodeOption(node);
    const formData = this.instance?.formData || {};
    const ctx = {deps: this.deps, users: this.userPool};
    return Object.keys(cloned).map((threadId) => {
      const conf = cloned[threadId] || {};
      const label = resolveCloneThreadLabel(threadId, nodeId, formData, opt, ctx);
      const keyPart = threadId.indexOf(nodeId + '_') === 0 ?
        threadId.slice(nodeId.length + 1) : threadId;
      let assignees = asIdList(conf.executors);
      if (!assignees.length) {
        assignees = resolveClonedDefaultUsers(
          opt.clonedDefaultUsers,
          keyPart,
          opt['default-users'],
        );
      }
      return {
        threadId,
        cloneKey: label,
        name: `${nodeLabel(node)} · ${label}`,
        assignees,
      };
    }).filter((t) => t.assignees.length > 0);
  }

  /** 同节点并行线程是否都已结束 */
  _parallelThreadsSettled(nodeId, excludeTaskId) {
    const related = (this.instance.tasks || []).filter((t) =>
      t.nodeId === nodeId && t.parallel && t.id !== excludeTaskId,
    );
    if (!related.length) return true;
    return related.every((t) => t.status !== 'pending');
  }

  /**
   * 激活下一节点：
   * - 抄送：通知节点，上一节点结束即自动完成，不产生待办
   * - clone 审批：拆成并行 threads
   */
  _activateNode(next, {fromThreadId} = {}) {
    const nt = nodeType(next);
    if (nt === 'finish_node') {
      this._closeProcess(next);
      return;
    }

    // 抄送 = 通知：不进任务池，只记一条系统抄送记录
    if (nt === 'carbon_node') {
      this._activateCarbonNotify(next);
      return;
    }

    if (this._isCloneNode(next)) {
      const threads = this._getCloneThreads(next);
      if (!threads.length) {
        throw new Error(`克隆节点「${nodeLabel(next)}」未配置各分类负责人，请返回配置`);
      }
      // 已有该节点 pending 则不重复开启
      const hasPending = this.instance.tasks.some(
        (t) => t.nodeId === next.id && t.status === 'pending',
      );
      if (hasPending) return;

      threads.forEach((th) => {
        const task = {
          id: uid('task'),
          nodeId: next.id,
          threadId: th.threadId,
          cloneKey: th.cloneKey,
          type: nt,
          name: th.name,
          assignees: th.assignees,
          status: 'pending',
          parallel: true,
          weak: nt === 'approval_node' && !!nodeOption(next).weak,
        };
        this._attachTimeoutMeta(task, next);
        this.instance.tasks.push(task);
      });
      this._pushHistory({
        created_by: 'system',
        action: 'fork',
        content: `启用多个子流程（${threads.map((t) => t.cloneKey).join('、')}）`,
        nodeId: next.id,
        nodeName: nodeLabel(next),
      });
      return;
    }

    const exists = this.instance.tasks.some(
      (t) => t.nodeId === next.id && t.status === 'pending',
    );
    if (exists) return;

    const task = {
      id: uid('task'),
      nodeId: next.id,
      type: nt,
      name: nodeLabel(next),
      assignees: this._resolveAssignees(next),
      status: 'pending',
      parallel: false,
      weak: nt === 'approval_node' && !!nodeOption(next).weak,
      threadId: fromThreadId || null,
    };
    this._attachTimeoutMeta(task, next);
    this.instance.tasks.push(task);
  }

  _userDisplayName(id) {
    const sid = String(id || '');
    if (!sid) return '';
    const u = (this.userPool || []).find((x) => String(x.id) === sid);
    return (u && u.name) || sid;
  }

  /**
   * 流程闭合：写在用户执行记录之后；不阻断上一节点撤回
   */
  _closeProcess(finishNode) {
    if (!this.instance || !finishNode) return;
    const fid = finishNode.id;
    if (!(this.instance.completedNodeIds || []).includes(fid)) {
      this.instance.completedNodeIds.push(fid);
    }
    // 稍晚于用户操作时间，保证历史顺序：用户执行 → 流程闭合
    const at = new Date(Date.now() + 1).toISOString();
    const name = nodeLabel(finishNode);
    // 避免重复闭合记录
    const hist = this.instance.history || [];
    const dup = hist.some((h) => h && h.action === 'finish' && h.nodeId === fid);
    if (!dup) {
      this.instance.history.push({
        id: uid('h'),
        created_by: 'system',
        created_at: at,
        action: 'finish',
        content: `到达结束节点「${name}」，流程闭合`,
        nodeId: fid,
        nodeName: name,
        meta: {closed: true},
      });
    }
    this.instance.status = '已完成';
  }

  /** 撤销因上一节点提交而触发的自动抄送 / 流程闭合 */
  _undoAutoFollowups(fromNodeId) {
    if (!this.instance || !fromNodeId) return;
    const outs = this.getOutgoing(fromNodeId, 'submit');
    outs.forEach((edge) => {
      const next = this.getNode(edge.targetNodeId);
      if (!next) return;
      const nt = nodeType(next);
      if (nt !== 'finish_node' && nt !== 'carbon_node') return;
      const completed = this.instance.completedNodeIds || [];
      const idx = completed.indexOf(next.id);
      if (idx >= 0) completed.splice(idx, 1);
      if (Array.isArray(this.instance.history)) {
        this.instance.history = this.instance.history.filter((h) => {
          if (!h || h.nodeId !== next.id) return true;
          if (nt === 'finish_node' && h.action === 'finish') return false;
          if (nt === 'carbon_node' && h.action === 'carbon_notify') return false;
          return true;
        });
      }
      if (nt === 'carbon_node') {
        // 不精确回滚 ccRecipients（其它抄送可能仍有效）；闭合状态下面清
      }
    });
    if (this.instance.status === '已完成') {
      const stillClosed = (this.instance.completedNodeIds || []).some((id) => {
        const n = this.getNode(id);
        return n && nodeType(n) === 'finish_node';
      });
      if (!stillClosed) {
        this.instance.status = '审批中';
      }
    }
  }

  /**
   * 抄送目标（可含克隆分类）：不产生并行任务，仅用于通知文案与接收人
   */
  _getCarbonNotifyTargets(node) {
    const nodeId = node.id;
    const nd = (this.instance?.nodeData || {})[nodeId] || {};
    const opt = nodeOption(node);
    const ctx = {deps: this.deps, users: this.userPool};

    if (this._isCloneNode(node)) {
      const cloned = nd.cloned || {};
      const keys = Object.keys(cloned);
      if (keys.length) {
        return keys.map((threadId) => {
          const conf = cloned[threadId] || {};
          const label = resolveCloneThreadLabel(threadId, nodeId,
            this.instance?.formData || {}, opt, ctx);
          const keyPart = threadId.indexOf(nodeId + '_') === 0 ?
            threadId.slice(nodeId.length + 1) : threadId;
          let assignees = asIdList(conf.executors);
          if (!assignees.length) {
            assignees = resolveClonedDefaultUsers(
              opt.clonedDefaultUsers,
              keyPart,
              opt['default-users'],
            );
          }
          return {cloneKey: label, assignees};
        });
      }
    }

    return [{
      cloneKey: null,
      assignees: this._resolveAssignees(node),
    }];
  }

  /**
   * 上一节点结束后自动抄送：不创建待办，只写系统通知历史
   */
  _activateCarbonNotify(next) {
    if (!next || !this.instance) return;
    if ((this.instance.completedNodeIds || []).includes(next.id)) return;

    // 清掉旧版遗留的抄送 pending 任务
    this.instance.tasks = (this.instance.tasks || []).filter((t) => {
      if (t.nodeId !== next.id) return true;
      return t.status !== 'pending';
    });

    const targets = this._getCarbonNotifyTargets(next);
    const cloneLabels = targets.map((t) => t.cloneKey).filter(Boolean);
    const recipientIds = [];
    targets.forEach((t) => {
      (t.assignees || []).forEach((id) => {
        const sid = String(id);
        if (sid && !recipientIds.includes(sid)) recipientIds.push(sid);
      });
    });
    const names = recipientIds.map((id) => this._userDisplayName(id)).filter(Boolean);

    let content;
    if (cloneLabels.length > 1) {
      content = `系统自动抄送多个（${cloneLabels.join('、')}）`;
    } else if (cloneLabels.length === 1) {
      content = `系统自动抄送单个（${cloneLabels[0]}）`;
    } else if (names.length > 1) {
      content = `系统自动抄送多个（${names.join('、')}）`;
    } else if (names.length === 1) {
      content = `系统自动抄送单个（${names[0]}）`;
    } else {
      content = `系统自动抄送「${nodeLabel(next)}」`;
    }

    this._pushHistory({
      created_by: 'system',
      action: 'carbon_notify',
      content,
      nodeId: next.id,
      nodeName: nodeLabel(next),
      meta: {
        notify: true,
        recipients: recipientIds,
        cloneLabels,
      },
    });

    this.instance.ccRecipients = [
      ...new Set([
        ...(this.instance.ccRecipients || []).map(String),
        ...recipientIds,
      ]),
    ];
    if (!this.instance.completedNodeIds.includes(next.id)) {
      this.instance.completedNodeIds.push(next.id);
    }
  }

  /** 审批节点挂上 deadline / 时效类型 */
  _attachTimeoutMeta(task, node) {
    if (nodeType(node) !== 'approval_node') return;
    const opt = nodeOption(node);
    const ms = resolveTimeoutMs(opt);
    if (!ms) return;
    const now = Date.now();
    task.activatedAt = new Date(now).toISOString();
    task.deadlineAt = new Date(now + ms).toISOString();
    task.timeoutType = resolveTimeoutType(opt);
    task.timeoutWarning = false;
  }

  /**
   * 检查 pending 审批时效：
   * - 仅预警：标记 timeoutWarning
   * - 强制关闭：自动超时跳过并推进后续节点
   * @returns {object|null} 有变更时返回 getState()，否则 null
   */
  tickTimeouts(nowMs) {
    if (!this.instance) return null;
    const now = nowMs != null ? nowMs : Date.now();
    let changed = false;
    const pending = (this.instance.tasks || []).filter(
      (t) => t.status === 'pending' && t.deadlineAt,
    );
    // 先处理强制关闭（可能改 tasks），再处理预警
    pending.forEach((task) => {
      const due = new Date(task.deadlineAt).getTime();
      if (Number.isNaN(due) || due > now) return;
      const node = this.getNode(task.nodeId);
      const type = task.timeoutType || resolveTimeoutType(nodeOption(node));
      if (type === '强制关闭') {
        if (task.status !== 'pending') return;
        this._timeoutForceSkip(task, node);
        changed = true;
      } else if (!task.timeoutWarning) {
        task.timeoutWarning = true;
        changed = true;
        this._pushHistory({
          created_by: 'system',
          action: 'timeout_warn',
          content: `节点「${task.name}」已超时（仅预警）`,
          nodeId: task.nodeId,
          nodeName: task.name,
          meta: {timeout: true, warning: true},
        });
      }
    });
    if (!changed) return null;
    this.instance.updated_at = new Date().toISOString();
    return this.getState();
  }

  /** 强制关闭：超时跳过当前审批并沿 submit 边推进 */
  _timeoutForceSkip(task, node) {
    task.status = 'timeout_skipped';
    task.timeoutWarning = true;
    const threadHint = task.cloneKey ? `〔线程:${task.cloneKey}〕` : '';
    this._pushHistory({
      created_by: 'system',
      action: 'timeout_skip',
      content: `超时跳过「${task.name}」${threadHint}`,
      nodeId: task.nodeId,
      nodeName: task.name,
      threadId: task.threadId,
      cloneKey: task.cloneKey,
      meta: {skipped: true, timeout: true, status: '超时跳过'},
    });

    if (task.parallel && !this._parallelThreadsSettled(task.nodeId, task.id)) {
      this._refreshStatus();
      return;
    }

    if (!this.instance.completedNodeIds.includes(task.nodeId)) {
      this.instance.completedNodeIds.push(task.nodeId);
    }

    const outs = this.getOutgoing(task.nodeId, 'submit');
    outs.forEach((edge) => {
      const next = this.getNode(edge.targetNodeId);
      if (!next) return;
      try {
        this._activateNode(next);
      } catch (e) {
        this._pushHistory({
          created_by: 'system',
          action: 'error',
          content: e.message || String(e),
          nodeId: next.id,
          nodeName: nodeLabel(next),
        });
      }
    });
    this._refreshStatus();
  }

  _canActOnTask(actorId, task, node) {
    const assignees = this._taskAssignees(task, node);
    if (assignees.includes(String(actorId))) {
      return {ok: true, weakSkip: false};
    }
    return {ok: false, weakSkip: false};
  }

  /**
   * 若存在 pending 的 weak 审批，且当前用户是其「下一节点」执行人，
   * 则允许直接对下一节点发起动作（跨过 weak）。
   */
  _appendWeakBypassActions(actorId, actions) {
    const pending = (this.instance.tasks || []).filter((t) => t.status === 'pending');
    const weakTasks = pending.filter((t) => {
      const n = this.getNode(t.nodeId);
      return n && nodeType(n) === 'approval_node' && !!nodeOption(n).weak;
    });
    if (!weakTasks.length) return;

    weakTasks.forEach((wt) => {
      const outs = this.getOutgoing(wt.nodeId, 'submit');
      outs.forEach((edge) => {
        const next = this.getNode(edge.targetNodeId);
        if (!next) return;
        const nt = nodeType(next);
        if (nt === 'finish_node') return;
        // 抄送是通知节点，不制造待办（真正推进时由上一节点出边自动抄送）
        if (nt === 'carbon_node') return;

        // 已有该节点 pending 任务则 getAvailableActions 主逻辑会处理；这里补「尚未进 tasks」的情况
        const already = pending.some((t) => t.nodeId === next.id);
        const alreadyAction = actions.some((a) => a.nodeId === next.id && a.key !== 'cancel');
        if (already && alreadyAction) return;

        const fakeTask = {
          id: already ? pending.find((t) => t.nodeId === next.id).id : uid('task'),
          nodeId: next.id,
          type: nt,
          name: nodeLabel(next),
          assignees: this._taskAssignees({assignees: []}, next),
          status: 'pending',
          weak: !!nodeOption(next).weak,
        };
        if (!fakeTask.assignees.includes(String(actorId))) return;

        // 标记需跨过的 weak 任务
        const bypass = {
          key: nt === 'approval_node' ? 'approve' : 'submit',
          label: nt === 'approval_node' ? '跳过弱审批并同意' : '跳过弱审批并提交',
          taskId: fakeTask.id,
          nodeId: next.id,
          primary: true,
          weakSkip: true,
          bypassWeakTaskIds: [wt.id],
          _virtualTask: already ? null : fakeTask,
        };
        if (!actions.some((a) => a.nodeId === bypass.nodeId && a.key === bypass.key)) {
          actions.push(bypass);
        }
      });
    });
  }

  /**
   * 推进流程
   * @param {{ action: string, data?: object, actorId?: string, taskId?: string, nodeId?: string, comment?: string }} payload
   */
  patch(payload = {}) {
    if (!this.instance) {
      throw new Error('请先 init 本地实例');
    }
    const action = payload.action;
    if (!action) {
      throw new Error('缺少 action');
    }

    const actorId = payload.actorId != null ? String(payload.actorId) : this.actorId;
    if (!actorId) {
      throw new Error('未选择操作身份');
    }
    this.actorId = actorId;

    if (action === 'cancel') {
      return this.cancel({actorId});
    }

    const available = this.getAvailableActions(actorId);
    const matched = available.find((a) => {
      if (a.key !== action) return false;
      if (payload.taskId && a.taskId !== payload.taskId) return false;
      if (payload.nodeId && a.nodeId !== payload.nodeId) return false;
      return true;
    }) || available.find((a) => a.key === action);

    if (!matched) {
      throw new Error('当前身份无权执行该操作，或无可执行任务');
    }

    // 快照用于撤回（含节点/任务，便于克隆线程各自撤回）
    const snapshot = toPlain(this.instance);
    this._cancelStack.push({
      actorId,
      snapshot,
      at: Date.now(),
      nodeId: matched.nodeId || (matched._virtualTask && matched._virtualTask.nodeId) || null,
      taskId: matched.taskId || (matched._virtualTask && matched._virtualTask.id) || null,
      threadId: (matched._virtualTask && matched._virtualTask.threadId) || null,
    });

    if (payload.data && typeof payload.data === 'object') {
      this.instance.formData = {
        ...this.instance.formData,
        ...toPlain(payload.data),
      };
    }

    // 虚拟后继任务先挂上
    if (matched._virtualTask) {
      this.instance.tasks.push(matched._virtualTask);
    }

    // 跨过 weak：先写历史并完成 weak 任务；顺带触发其抄送通知边
    const bypassIds = matched.bypassWeakTaskIds || [];
    bypassIds.forEach((tid) => {
      const task = this.instance.tasks.find((t) => t.id === tid);
      if (!task || task.status !== 'pending') return;
      const node = this.getNode(task.nodeId);
      task.status = 'skipped';
      this.instance.completedNodeIds.push(task.nodeId);
      this._pushHistory({
        created_by: actorId,
        action: 'weak_skip',
        content: `跳过「${task.name || nodeLabel(node)}」`,
        nodeId: task.nodeId,
        nodeName: task.name,
        meta: {weak: true, skipped: true},
      });
      this.getOutgoing(task.nodeId, 'submit').forEach((edge) => {
        const n = this.getNode(edge.targetNodeId);
        if (n && nodeType(n) === 'carbon_node') {
          this._activateCarbonNotify(n);
        }
      });
    });

    const task = this.instance.tasks.find((t) => t.id === matched.taskId) ||
      this.instance.tasks.find((t) => t.nodeId === matched.nodeId && t.status === 'pending');
    if (!task) {
      // 回滚快照
      this.instance = snapshot;
      this._cancelStack.pop();
      throw new Error('未找到对应待办任务');
    }

    // 补全撤回元数据（含克隆线程）
    const cancelEntry = this._cancelStack[this._cancelStack.length - 1];
    if (cancelEntry) {
      cancelEntry.taskId = task.id;
      cancelEntry.nodeId = task.nodeId;
      cancelEntry.threadId = task.threadId || null;
    }

    const node = this.getNode(task.nodeId);
    const comment = payload.comment || '';
    const attachments = Array.isArray(payload.attachments) ? toPlain(payload.attachments) : [];

    if (action === 'submit' || action === 'approve' || action === 'carbon') {
      this._completeTaskForward(task, node, actorId, action, comment, attachments);
    } else if (action === 'reject') {
      this._completeTaskReject(task, node, actorId, comment, attachments);
    } else {
      this.instance = snapshot;
      this._cancelStack.pop();
      throw new Error(`不支持的 action: ${action}`);
    }

    this.instance.updated_at = new Date().toISOString();
    this.instance.canCancel = true;
    // 只允许撤回「刚刚自己」的一步，且尚未再次 patch
    this.instance.actorId = actorId;

    return this.getState();
  }

  _pushHistory(item) {
    this.instance.history.push({
      id: uid('h'),
      created_at: new Date().toISOString(),
      ...item,
    });
  }

  _completeTaskForward(task, node, actorId, action, comment, attachments = []) {
    task.status = 'done';
    if (nodeType(node) === 'start_node') {
      this.instance.created_by = actorId;
    }

    const actionLabel =
      action === 'approve' ? '同意' :
      action === 'carbon' ? '已阅抄送' :
      '提交';
    const threadHint = task.cloneKey ? `〔线程:${task.cloneKey}〕` : '';
    const createdAt = new Date().toISOString();
    this._pushHistory({
      created_by: actorId,
      action,
      content: `${actionLabel}「${task.name}」${threadHint}${comment ? '：' + comment : ''}`,
      nodeId: task.nodeId,
      nodeName: task.name,
      threadId: task.threadId,
      cloneKey: task.cloneKey,
      attachments,
      created_at: createdAt,
    });

    // 节点执行后：按访问配置的值绑定写入表单
    this._applyValueBinds(task, node, actorId, createdAt, {comment, action});

    // 并行线程：同节点还有 pending 则不往下走
    if (task.parallel && !this._parallelThreadsSettled(task.nodeId, task.id)) {
      this._refreshStatus();
      return;
    }

    if (!this.instance.completedNodeIds.includes(task.nodeId)) {
      this.instance.completedNodeIds.push(task.nodeId);
    }
    if (task.parallel) {
      this._pushHistory({
        created_by: 'system',
        action: 'join',
        content: `并行线程已全部完成「${nodeLabel(node)}」`,
        nodeId: task.nodeId,
        nodeName: nodeLabel(node),
      });
    }

    const outs = this.getOutgoing(task.nodeId, action === 'reject' ? 'reject' : 'submit');
    if (!outs.length) {
      if (nodeType(node) === 'finish_node') {
        this._closeProcess(node);
      }
      this._refreshStatus();
      return;
    }

    // 先激活普通/抄送节点，结束节点最后闭合，保证历史：用户执行 → … → 流程闭合
    const nextList = [];
    outs.forEach((edge) => {
      const next = this.getNode(edge.targetNodeId);
      if (next) nextList.push(next);
    });
    const finishList = nextList.filter((n) => nodeType(n) === 'finish_node');
    const otherList = nextList.filter((n) => nodeType(n) !== 'finish_node');
    [...otherList, ...finishList].forEach((next) => {
      try {
        this._activateNode(next);
      } catch (e) {
        this._pushHistory({
          created_by: 'system',
          action: 'error',
          content: e.message || String(e),
          nodeId: next.id,
          nodeName: nodeLabel(next),
        });
      }
    });

    this._refreshStatus();
  }

  _completeTaskReject(task, node, actorId, comment, attachments = []) {
    task.status = 'rejected';
    const threadHint = task.cloneKey ? `〔线程:${task.cloneKey}〕` : '';
    const createdAt = new Date().toISOString();
    this._pushHistory({
      created_by: actorId,
      action: 'reject',
      content: `退回「${task.name}」${threadHint}${comment ? '：' + comment : ''}`,
      nodeId: task.nodeId,
      nodeName: task.name,
      threadId: task.threadId,
      cloneKey: task.cloneKey,
      attachments,
      created_at: createdAt,
    });

    this._applyValueBinds(task, node, actorId, createdAt, {
      comment,
      action: 'reject',
    });

    // 并行线程中任一退回：取消同节点其余 pending 线程
    if (task.parallel) {
      (this.instance.tasks || []).forEach((t) => {
        if (t.nodeId === task.nodeId && t.parallel && t.id !== task.id && t.status === 'pending') {
          t.status = 'cancelled';
        }
      });
    }

    const outs = this.getOutgoing(task.nodeId, 'reject');
    if (outs.length) {
      outs.forEach((edge) => {
        const next = this.getNode(edge.targetNodeId);
        if (!next) return;
        try {
          this._activateNode(next);
        } catch (e) {
          this._pushHistory({
            created_by: 'system',
            action: 'error',
            content: e.message || String(e),
            nodeId: next.id,
            nodeName: nodeLabel(next),
          });
        }
      });
    } else {
      // 无退回边：退回开始节点
      const start = this.getStartNode();
      if (start) {
        try {
          this._activateNode(start);
        } catch (e) {
          this._pushHistory({
            created_by: 'system',
            action: 'error',
            content: e.message || String(e),
            nodeId: start.id,
            nodeName: nodeLabel(start),
          });
        }
      }
    }
    this.instance.status = '已退回';
    this._refreshStatus();
  }

  _resolveAssignees(node) {
    const opt = nodeOption(node);
    const nd = (this.instance?.nodeData || {})[node.id] || {};
    const fromConfig = asIdList(nd.executors);
    if (fromConfig.length) return fromConfig;
    const defaults = asIdList(opt['default-users']);
    if (defaults.length) return defaults;
    if (nodeType(node) === 'start_node' && this.instance?.created_by) {
      return [String(this.instance.created_by)];
    }
    return this.actorId ? [this.actorId] : [];
  }

  _refreshStatus() {
    // 已完成后若结束节点被撤回撤销，需重新计算
    const hasFinish = (this.instance.completedNodeIds || []).some((id) => {
      const n = this.getNode(id);
      return n && nodeType(n) === 'finish_node';
    });
    if (this.instance.status === '已完成' && hasFinish) return;
    if (this.instance.status === '已完成' && !hasFinish) {
      this.instance.status = '审批中';
    }

    const pending = (this.instance.tasks || []).filter((t) => t.status === 'pending');
    if (!pending.length && this.instance.status !== '已退回') {
      this.instance.status = hasFinish ? '已完成' : '进行中';
      return;
    }
    if (this.instance.status === '待发起') {
      const startPending = pending.some((t) => t.type === 'start_node');
      if (!startPending) this.instance.status = '审批中';
    } else if (this.instance.status !== '已退回') {
      this.instance.status = '审批中';
    }
  }

  /**
   * 撤回自己上次提交：
   * - 下一节点已处理后不可撤
   * - 克隆平行线程可各自撤回（手术式还原本线程，互不影响）
   * - 非平行节点用快照整单还原
   */
  cancel({actorId, cancelIndex} = {}) {
    if (!this.instance) {
      throw new Error('请先 init 本地实例');
    }
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!this._cancelStack.length) {
      throw new Error('当前不可撤回');
    }

    let index = cancelIndex;
    let entry = index != null ? this._cancelStack[index] : null;
    if (entry == null || String(entry.actorId) !== String(aid)) {
      const found = this._findCancelEntry(aid);
      if (!found) {
        throw new Error('只能撤回自己提交且下一节点尚未处理的操作');
      }
      entry = found.entry;
      index = found.index;
    }

    if (this._hasDownstreamProgress(entry)) {
      throw new Error('下一节点已处理，无法撤回');
    }

    if (index !== this._cancelStack.length - 1) {
      throw new Error('只能撤回最后一次提交');
    }

    const task = (this.instance.tasks || []).find((t) => t.id === entry.taskId);
    const useSurgical = !!(task && task.parallel);

    if (useSurgical) {
      this._surgicalCancelParallel(entry);
      this._cancelStack.pop();
    } else {
      this._cancelStack.pop();
      this.instance = entry.snapshot;
    }

    // 撤回为正常操作：不写历史记录（快照还原已带回撤前历史；手术式撤回需删对应提交记录）
    if (useSurgical) {
      this._removeHistoryForCancel(entry);
    }

    this.instance.canCancel = this._cancelStack.some((e) =>
      !this._hasDownstreamProgress(e));
    this.instance.updated_at = new Date().toISOString();
    this._refreshStatus();
    return this.getState();
  }

  /** 手术式撤回时去掉该次提交产生的历史（不留「撤回」痕迹） */
  _removeHistoryForCancel(entry) {
    if (!entry || !this.instance || !Array.isArray(this.instance.history)) return;
    const hist = this.instance.history;
    for (let i = hist.length - 1; i >= 0; i--) {
      const h = hist[i];
      if (!h || h.created_by === 'system') continue;
      if (entry.nodeId && h.nodeId !== entry.nodeId) continue;
      if (entry.threadId && h.threadId && h.threadId !== entry.threadId) continue;
      if (['submit', 'approve', 'reject', 'carbon'].includes(h.action)) {
        hist.splice(i, 1);
        break;
      }
    }
  }

  /** 开始节点绑定的表单 id */
  getStartFormId() {
    const start = this.getStartNode();
    return nodeOption(start).view_id || null;
  }

  getStartAcl() {
    const start = this.getStartNode();
    return unpackAclMap(nodeOption(start).acl);
  }

  /** 指定节点的视图 / ACL
   * - 有明确节点时：用该节点自身 ACL（空对象表示按默认全可见可编辑语义，不回退开始节点）
   * - 无节点时：回退开始节点 ACL
   */
  getNodeViewId(nodeId) {
    const node = nodeId ? this.getNode(nodeId) : null;
    const viewId = node ? (nodeOption(node).view_id || null) : null;
    return viewId || this.getStartFormId();
  }

  getNodeAcl(nodeId) {
    const node = nodeId ? this.getNode(nodeId) : null;
    if (node) {
      return unpackAclMap(nodeOption(node).acl);
    }
    return this.getStartAcl();
  }

  getNodeBinds(nodeId) {
    const node = nodeId ? this.getNode(nodeId) : null;
    if (!node) return {};
    return unpackBindsMap(nodeOption(node).acl);
  }

  /**
   * 节点执行完成后，按 binds（默认值）写入 formData
   * 与可见性无关：字段 acl=2（不可见）时仍会写入
   * - executor → 操作人 id
   * - created_at → 本次执行时间
   * - clone_value → 当前并行线程对应的克隆对象原始值
   * - opinion → 审批意见文本
   * - agree → 是否同意（同意/不同意）
   */
  _applyValueBinds(task, node, actorId, createdAt, extra = {}) {
    if (!this.instance || !node) return;
    const binds = unpackBindsMap(nodeOption(node).acl);
    const keys = Object.keys(binds || {});
    if (!keys.length) return;

    // 不读取 ACL：隐藏字段同样落库
    const formData = toPlain(this.instance.formData) || {};
    const stamp = createdAt || new Date().toISOString();
    const comment = extra.comment != null ? String(extra.comment) : '';
    const action = extra.action || '';
    keys.forEach((fullkey) => {
      const bind = binds[fullkey];
      if (!bind) return;
      let val;
      if (bind === BIND_EXECUTOR) {
        val = actorId != null ? String(actorId) : '';
      } else if (bind === BIND_CREATED_AT) {
        val = stamp;
      } else if (bind === BIND_CLONE_VALUE) {
        val = this._resolveTaskCloneValue(task, node);
      } else if (bind === BIND_OPINION) {
        val = comment;
      } else if (bind === BIND_AGREE) {
        val = action === 'reject' ? '不同意' : '同意';
      } else {
        return;
      }
      setByPath(formData, fullkey, val);
    });
    this.instance.formData = formData;
  }

  /** 从当前 clone 线程反查克隆字段原始值 */
  _resolveTaskCloneValue(task, node) {
    if (!task || !node) return null;
    const opt = nodeOption(node);
    const clonedBy = opt.clonedBy;
    if (!clonedBy) return null;
    const path = typeof clonedBy === 'string' ? clonedBy : (clonedBy.key || '');
    if (!path) return null;

    const threadId = task.threadId || '';
    const suffix = threadId.indexOf(node.id + '_') === 0 ?
      threadId.slice(node.id.length + 1) :
      (task.cloneKey || threadId);
    if (!suffix && suffix !== 0) return null;

    const listRaw = getByPath(this.instance.formData || {}, path);
    const list = Array.isArray(listRaw) ?
      listRaw :
      (listRaw != null && listRaw !== '' ? [listRaw] : []);
    const ctx = {
      deps: this.deps,
      users: this.userPool,
      clonedType: opt.clonedType,
    };
    const hit = list.find((item) => {
      const meta = resolveCloneItemMeta(item, clonedBy, ctx);
      return meta.keyPart === String(suffix);
    });
    return hit != null ? hit : null;
  }

  /**
   * 当前身份最近一次已处理的任务（含克隆并行线程）
   * 用于提交后仍按该节点 ACL 只读展示表单
   */
  getLastParticipatedTaskForActor(actorId) {
    if (!this.instance) return null;
    const aid = actorId != null ? String(actorId) : this.actorId;
    if (!aid) return null;

    const hist = this.instance.history || [];
    for (let i = hist.length - 1; i >= 0; i--) {
      const h = hist[i];
      if (!h || String(h.created_by) !== aid) continue;
      if (!['submit', 'approve', 'reject', 'carbon'].includes(h.action)) continue;
      if (!h.nodeId) continue;

      const node = this.getNode(h.nodeId);
      if (!node) continue;

      const candidates = (this.instance.tasks || []).filter((t) => {
        if (t.nodeId !== h.nodeId) return false;
        if (h.threadId && t.threadId && t.threadId !== h.threadId) return false;
        return this._isActorAssignee(aid, t, node);
      });
      const done = candidates.find((t) =>
        t.status === 'done' || t.status === 'rejected' || t.status === 'skipped') ||
        candidates[0];
      if (done) {
        return {
          ...done,
          peerView: true,
          status: done.status === 'pending' ? 'done' : done.status,
        };
      }

      // 任务已被汇聚清理时，用历史拼只读上下文
      return {
        id: 'hist-' + (h.id || i),
        nodeId: h.nodeId,
        threadId: h.threadId || null,
        cloneKey: h.cloneKey || null,
        type: nodeType(node),
        name: h.nodeName || nodeLabel(node),
        assignees: [aid],
        status: 'done',
        parallel: !!(h.threadId || this._isCloneNode(node)),
        peerView: true,
      };
    }
    return null;
  }

  /**
   * 供表单（尤其 MultiForm）按平行线程过滤/锁定行
   * - parallelVisible=false：只展示本人行
   * - parallelVisible=true：展示全部，仅本人行可编辑
   * - 发起人：始终展示全部
   * - 已提交任务：只读（canEditOwn=false）
   */
  getCloneFormContext(task) {
    if (!this.instance || !task || !task.parallel) return null;
    const node = this.getNode(task.nodeId);
    if (!node || !this._isCloneNode(node)) return null;
    const opt = nodeOption(node);
    const clonedBy = opt.clonedBy;
    const clonedByPath = typeof clonedBy === 'string' ? clonedBy : (clonedBy && clonedBy.key) || '';
    const threadId = task.threadId || '';
    const keyPart = threadId.indexOf(node.id + '_') === 0 ?
      threadId.slice(node.id.length + 1) :
      String(task.cloneKey || '');
    if (!keyPart && keyPart !== 0) return null;

    const aid = this.actorId != null ? String(this.actorId) : '';
    const isCreator = !!(aid && String(this.instance.created_by) === aid);
    const mine = this._isActorAssignee(aid, task, node);
    const parallelVisible = this._isParallelVisible(node);
    const settled = task.status && task.status !== 'pending';
    const peerView = !!task.peerView || !mine || settled;

    return {
      keyPart: String(keyPart),
      threadId: threadId || null,
      cloneKey: task.cloneKey || null,
      cloneValue: this._resolveTaskCloneValue(task, node),
      clonedBy,
      clonedByPath,
      clonedType: opt.clonedType || '',
      parallelVisible,
      /** 展示全部平行行（否则只展示本人） */
      showAllRows: parallelVisible || isCreator,
      /** 是否允许编辑本人行（已提交不可编辑） */
      canEditOwn: mine && !peerView && !settled,
      isCreator,
      nodeId: node.id,
      readOnly: settled || peerView,
    };
  }

  /** 导出可持久化快照（流程测试 localStorage） */
  serialize() {
    return toPlain({
      instance: this.instance,
      actorId: this.actorId,
      cancelStack: this._cancelStack || [],
    });
  }

  /** 从快照恢复运行态 */
  hydrate(snapshot = {}) {
    if (!snapshot || !snapshot.instance) {
      throw new Error('无效的测试缓存');
    }
    this.instance = toPlain(snapshot.instance);
    this.actorId = snapshot.actorId != null ?
      String(snapshot.actorId) :
      (this.instance.actorId != null ? String(this.instance.actorId) : this.actorId);
    this._cancelStack = Array.isArray(snapshot.cancelStack) ?
      toPlain(snapshot.cancelStack) :
      [];
    if (this.instance) {
      this.instance.actorId = this.actorId;
    }
    return this.getState();
  }
}
